import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Edit, Eye, Loader2, Plus, RefreshCw, Save, Search, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";
import axios from "../../api/axios";
import * as XLSX from "xlsx";

// Import unified PaginationSearchbar component
import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

// Import central theme classes from MasterCssClass.js
import {
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBtnBack,
  masterFormBtnSubmit,
  masterBadgeLive,
  masterBadgeInactive,
  masterActionBtnEdit,
  masterActionBtnDelete,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
  masterSelect,
  masterInput,
  masterInputLabel,
  masterFormCard,
  masterFormFieldGroup,
  masterSubCard,
  masterInnerCard,
  masterActionButton,
  masterDeleteButton,
} from "../../GlobalComponents/MasterCssClass";

const toastErrorOnce = (msg) => toast.error(msg, { id: "one-error" });
const toastOkOnce = (msg) => toast.success(msg, { id: "one-success" });

const emptyQuestion = {
  marks: "",
  negativeMarks: "",
  questionText: "",
  optionA: "",
  optionB: "",
  optionC: "",
  optionD: "",
  correctOption: "",
  explanationText: "",
  questionImages: [],
  optionImages: { A: [], B: [], C: [], D: [] },
  explanationImages: [],
};

const isNoCategoryValue = (value) => {
  const v = String(value || "").trim().toUpperCase();
  return !v || v === "NO CATEGORY" || v === "NO_CATEGORY" || v === "NONE";
};

const cleanMathText = (value) => {
  const text = String(value || "");
  return text
    .replace(/\\\[/g, "$$$")
    .replace(/\\\]/g, "$$$")
    .replace(/\\\(/g, "$")
    .replace(/\\\)/g, "$")
    .trim();
};

const normalizeCorrectOption = (value) => {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";
  if (["1", "A", "OPTION A", "A)"].includes(raw) || raw.startsWith("A")) return "A";
  if (["2", "B", "OPTION B", "B)"].includes(raw) || raw.startsWith("B")) return "B";
  if (["3", "C", "OPTION C", "C)"].includes(raw) || raw.startsWith("C")) return "C";
  if (["4", "D", "OPTION D", "D)"].includes(raw) || raw.startsWith("D")) return "D";
  return "";
};

const MathField = ({ fieldKey, label = "", value, onChange, onPaste, onFocus, inputRef, placeholder = "", rows = 3, className = "" }) => (
  <div className={`space-y-1 p-1 ${className}`}>
    {label ? <label className={masterInputLabel}>{label}</label> : null}
    <textarea
      ref={inputRef}
      onFocus={onFocus}
      value={value}
      onPaste={onPaste}
      onChange={(e) => onChange(e.target.value)}
      dir="ltr"
      rows={rows}
      className={`${masterInput} p-1 resize-y text-left`}
      placeholder={placeholder}
    />
    <div className="min-h-[36px] p-1 border border-blue-50 bg-blue-50/30 rounded-xl text-xs text-slate-800 whitespace-pre-wrap break-words leading-relaxed text-left">
      {value || <span className="text-slate-400 italic">Preview...</span>}
    </div>
  </div>
);

const QuestionBankMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [exams, setExams] = useState([]);
  const [symbolField, setSymbolField] = useState("");
  const [questionSetId, setQuestionSetId] = useState("");
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [filters, setFilters] = useState({ 
    examMasterId: "", 
    examCategoryId: "", 
    examStage: "", 
    subjectId: "", 
    topicName: "", 
    subTopicName: "", 
    nestedTopicName: "", 
    nestedSubTopicName: "", 
    status: "" 
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [viewMode, setViewMode] = useState("form");

  const [editId, setEditId] = useState("");
  
  const [baseForm, setBaseForm] = useState({ 
    examMasterId: "", 
    examCategoryId: "", 
    examStage: "", 
    subjectId: "", 
    topicName: "", 
    subTopicName: "", 
    nestedTopicName: "", 
    nestedSubTopicName: "", 
    status: "ACTIVE" 
  });

  const [questionForm, setQuestionForm] = useState({ ...emptyQuestion });
  const [draftQuestions, setDraftQuestions] = useState([]);
  const [editingDraftIndex, setEditingDraftIndex] = useState(null);
  const [entryMode, setEntryMode] = useState("MANUAL");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewSet, setViewSet] = useState(null);
  const [activeField, setActiveField] = useState("questionText");
  const fieldRefs = useRef({});
  const formMathRef = useRef(null);
  const questionEntryRef = useRef(null);
  const viewMathRef = useRef(null);
  const typesetTimerRef = useRef(null);

  const isNewRoute = location.pathname.endsWith("/question-bank/new");
  const isEditRoute = Boolean(id) && location.pathname.includes("/question-bank/") && location.pathname.endsWith("/edit");

  const closeModal = () => {
    setViewMode("list");
  };

  const handlePasteCleaning = (e, currentValue, setter) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const cleanedData = cleanMathText(pastedData);
    const start = e.target.selectionStart;
    const end = e.target.selectionEnd;
    setter(currentValue.substring(0, start) + cleanedData + currentValue.substring(end));
  };

  const headerToKey = (header) => {
    const key = String(header || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");
    if (["question", "questiontext", "ques", "q"].includes(key)) return "questionText";
    if (["optiona", "opta", "a", "option1", "choicea"].includes(key)) return "optionA";
    if (["optionb", "optb", "b", "option2", "choiceb"].includes(key)) return "optionB";
    if (["optionc", "optc", "c", "option3", "choicec"].includes(key)) return "optionC";
    if (["optiond", "optd", "d", "option4", "choiced"].includes(key)) return "optionD";
    if (["correctoption", "correct", "answer", "ans", "rightanswer", "correctanswer"].includes(key)) return "correctOption";
    if (["explanation", "solution", "explain", "rationale"].includes(key)) return "explanationText";
    if (["marks", "mark", "score", "points"].includes(key)) return "marks";
    if (["negativemarks", "negative", "negativemark", "minusmarks", "negativepoints"].includes(key)) return "negativeMarks";
    return "";
  };

  const symbolPalette = [
    "+", "−", "×", "÷", "=", "≠", "≈", "≡", "<", ">", "≤", "≥", "±", "∓",
    "√", "∛", "∜", "∑", "∏", "∫", "∬", "∭", "∂", "∇", "∞", "∝", "∠", "∟",
    "∈", "∉", "∅", "⊂", "⊃", "⊆", "⊇", "∪", "∩",
    "¬", "∧", "∨", "⇒", "⇔", "∀", "∃",
    "→", "←", "↑", "↓", "↔", "↕",
    "α","β","γ","δ","ε","ζ","η","θ","ι","κ","λ","μ","ν","ξ","ο","π","ρ","σ","τ","υ","φ","χ","ψ","ω",
    "Α","Β","Γ","Δ","Ε","Ζ","Η","Θ","Ι","Κ","Λ","Μ","Ν","Ξ","Ο","Π","Ρ","Σ","Τ","Υ","Φ","Χ","Ψ","Ω",
    "°", "℃", "℉", "Å", "mol", "Na", "Cl",
    "m","cm","mm","kg","g","s","ms","A","V","W","J","N","Pa","Hz",
    "′", "″", "…", "•", "∴", "∵"
  ];

  const fieldLabels = {
    questionText: "Question",
    optionA: "Option A",
    optionB: "Option B",
    optionC: "Option C",
    optionD: "Option D",
    explanationText: "Explanation",
  };

  const filteredCategories = useMemo(() => {
    if (!filters.examMasterId) return [];
    return categories.filter((cat) => String(cat.examCode || "").toUpperCase() === String(filters.examMasterId || "").toUpperCase());
  }, [categories, filters.examMasterId]);
  const filterNeedsCategory = useMemo(() => filteredCategories.length > 0, [filteredCategories]);

  const filteredSubjects = useMemo(() => {
    if (!filters.examMasterId) return [];
    const needsCategory = categories.some((cat) => String(cat.examCode || "").toUpperCase() === String(filters.examMasterId || "").toUpperCase());
    if (needsCategory && !filters.examCategoryId) return [];
    return subjects.filter(
      (s) =>
        String(s.examCode || "").toUpperCase() === String(filters.examMasterId || "").toUpperCase() &&
        (
          !needsCategory
            ? isNoCategoryValue(s.catId)
            : String(s.catId || "").toUpperCase() === String(filters.examCategoryId || "").toUpperCase()
        ) &&
        (!filters.examStage || String(s.examStage || "").toUpperCase() === String(filters.examStage || "").toUpperCase())
    );
  }, [categories, subjects, filters.examMasterId, filters.examCategoryId, filters.examStage]);

  const modalFilteredCategories = useMemo(() => {
    if (!baseForm.examMasterId) return [];
    return categories.filter((cat) => String(cat.examCode || "").toUpperCase() === String(baseForm.examMasterId || "").toUpperCase());
  }, [categories, baseForm.examMasterId]);
  const modalNeedsCategory = useMemo(() => modalFilteredCategories.length > 0, [modalFilteredCategories]);

  const modalFilteredSubjects = useMemo(() => {
    if (!baseForm.examMasterId) return [];
    const needsCategory = categories.some((cat) => String(cat.examCode || "").toUpperCase() === String(baseForm.examMasterId || "").toUpperCase());
    if (needsCategory && !baseForm.examCategoryId) return [];
    return subjects.filter(
      (s) =>
        String(s.examCode || "").toUpperCase() === String(baseForm.examMasterId || "").toUpperCase() &&
        (
          !needsCategory
            ? isNoCategoryValue(s.catId)
            : String(s.catId || "").toUpperCase() === String(baseForm.examCategoryId || "").toUpperCase()
        ) &&
        (!baseForm.examStage || String(s.examStage || "").toUpperCase() === String(baseForm.examStage || "").toUpperCase())
    );
  }, [categories, subjects, baseForm.examMasterId, baseForm.examCategoryId, baseForm.examStage]);

  const filterStageOptions = useMemo(() => {
    if (!filters.examMasterId || !filters.examCategoryId) return [];
    const selectedCategory = categories.find(
      (cat) =>
        String(cat.examCode || "").toUpperCase() === String(filters.examMasterId || "").toUpperCase() &&
        String(cat.catId || "").toUpperCase() === String(filters.examCategoryId || "").toUpperCase()
    );
    return Array.from(
      new Set(
        [ ...(Array.isArray(selectedCategory?.examStages) ? selectedCategory.examStages : []), selectedCategory?.examStage ]
          .map((s) => String(s || "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
  }, [categories, filters.examMasterId, filters.examCategoryId]);

  const modalStageOptions = useMemo(() => {
    if (!baseForm.examMasterId || !baseForm.examCategoryId) return [];
    const selectedCategory = categories.find(
      (cat) =>
        String(cat.examCode || "").toUpperCase() === String(baseForm.examMasterId || "").toUpperCase() &&
        String(cat.catId || "").toUpperCase() === String(baseForm.examCategoryId || "").toUpperCase()
    );
    return Array.from(
      new Set(
        [ ...(Array.isArray(selectedCategory?.examStages) ? selectedCategory.examStages : []), selectedCategory?.examStage ]
          .map((s) => String(s || "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
  }, [categories, baseForm.examMasterId, baseForm.examCategoryId]);
  
  const filterNeedsStage = useMemo(() => filterNeedsCategory && filterStageOptions.length > 0, [filterNeedsCategory, filterStageOptions]);
  const modalNeedsStage = useMemo(() => modalNeedsCategory && modalStageOptions.length > 0, [modalNeedsCategory, modalStageOptions]);

  const hasStageInRows = useMemo(
    () => rows.some((row) => String(row.examStage || "").trim()),
    [rows]
  );
  
  const hasTopicInRows = useMemo(
    () => rows.some((row) => String(row.topicName || "").trim() || String(row.subTopicName || "").trim() || String(row.nestedTopicName || "").trim()),
    [rows]
  );

  const filterTopicOptions = useMemo(() => {
    const selectedSubject = subjects.find(
      (s) => String(s.syllabusId || "").toUpperCase() === String(filters.subjectId || "").toUpperCase()
    );
    return (selectedSubject?.topics || []).map((t) => String(t.topicName || "").toUpperCase()).filter(Boolean);
  }, [subjects, filters.subjectId]);

  const filterSubTopicOptions = useMemo(() => {
    const selectedSubject = subjects.find(
      (s) => String(s.syllabusId || "").toUpperCase() === String(filters.subjectId || "").toUpperCase()
    );
    const selectedTopic = (selectedSubject?.topics || []).find(
      (t) => String(t.topicName || "").toUpperCase() === String(filters.topicName || "").toUpperCase()
    );
    return (selectedTopic?.subTopics || []).map((sub) => {
      if (typeof sub === "string") return sub.toUpperCase();
      return String(sub?.subTopicName || sub?.name || "").toUpperCase();
    }).filter(Boolean);
  }, [subjects, filters.subjectId, filters.topicName]);

  const filterNestedTopicOptions = useMemo(() => {
    const selectedSubject = subjects.find(
      (s) => String(s.syllabusId || "").toUpperCase() === String(filters.subjectId || "").toUpperCase()
    );
    const selectedTopic = (selectedSubject?.topics || []).find(
      (t) => String(t.topicName || "").toUpperCase() === String(filters.topicName || "").toUpperCase()
    );
    const selectedSub = (selectedTopic?.subTopics || []).find(
      (sub) => {
        const subName = typeof sub === "string" ? sub : (sub?.subTopicName || sub?.name || "");
        return String(subName).toUpperCase() === String(filters.subTopicName || "").toUpperCase();
      }
    );
    return (selectedSub?.nestedTopics || selectedSub?.childTopics || []).map((nt) => String(nt.nestedTopicName || nt.name || "").toUpperCase()).filter(Boolean);
  }, [subjects, filters.subjectId, filters.topicName, filters.subTopicName]);

  const filterNestedSubTopicOptions = useMemo(() => {
    const selectedSubject = subjects.find(
      (s) => String(s.syllabusId || "").toUpperCase() === String(filters.subjectId || "").toUpperCase()
    );
    const selectedTopic = (selectedSubject?.topics || []).find(
      (t) => String(t.topicName || "").toUpperCase() === String(filters.topicName || "").toUpperCase()
    );
    const selectedSub = (selectedTopic?.subTopics || []).find(
      (sub) => {
        const subName = typeof sub === "string" ? sub : (sub?.subTopicName || sub?.name || "");
        return String(subName).toUpperCase() === String(filters.subTopicName || "").toUpperCase();
      }
    );
    const selectedNested = (selectedSub?.nestedTopics || selectedSub?.childTopics || []).find(
      (nt) => String(nt.nestedTopicName || nt.name || "").toUpperCase() === String(filters.nestedTopicName || "").toUpperCase()
    );
    return (selectedNested?.nestedSubTopics || selectedNested?.childTopics || []).map((sst) => String(sst || "").toUpperCase()).filter(Boolean);
  }, [subjects, filters.subjectId, filters.topicName, filters.subTopicName, filters.nestedTopicName]);

  const modalTopicOptions = useMemo(() => {
    const selectedSubject = subjects.find(
      (s) => String(s.syllabusId || "").toUpperCase() === String(baseForm.subjectId || "").toUpperCase()
    );
    return (selectedSubject?.topics || []).map((t) => String(t.topicName || "").toUpperCase()).filter(Boolean);
  }, [subjects, baseForm.subjectId]);

  const modalSubTopicOptions = useMemo(() => {
    const selectedSubject = subjects.find(
      (s) => String(s.syllabusId || "").toUpperCase() === String(baseForm.subjectId || "").toUpperCase()
    );
    const selectedTopic = (selectedSubject?.topics || []).find(
      (t) => String(t.topicName || "").toUpperCase() === String(baseForm.topicName || "").toUpperCase()
    );
    return (selectedTopic?.subTopics || []).map((sub) => {
      if (typeof sub === "string") return sub.toUpperCase();
      return String(sub?.subTopicName || sub?.name || "").toUpperCase();
    }).filter(Boolean);
  }, [subjects, baseForm.subjectId, baseForm.topicName]);

  const modalNestedTopicOptions = useMemo(() => {
    const selectedSubject = subjects.find(
      (s) => String(s.syllabusId || "").toUpperCase() === String(baseForm.subjectId || "").toUpperCase()
    );
    const selectedTopic = (selectedSubject?.topics || []).find(
      (t) => String(t.topicName || "").toUpperCase() === String(baseForm.topicName || "").toUpperCase()
    );
    const selectedSub = (selectedTopic?.subTopics || []).find(
      (sub) => {
        const subName = typeof sub === "string" ? sub : (sub?.subTopicName || sub?.name || "");
        return String(subName).toUpperCase() === String(baseForm.subTopicName || "").toUpperCase();
      }
    );
    return (selectedSub?.nestedTopics || selectedSub?.childTopics || []).map((nt) => String(nt.nestedTopicName || nt.name || "").toUpperCase()).filter(Boolean);
  }, [subjects, baseForm.subjectId, baseForm.topicName, baseForm.subTopicName]);

  const modalNestedSubTopicOptions = useMemo(() => {
    const selectedSubject = subjects.find(
      (s) => String(s.syllabusId || "").toUpperCase() === String(baseForm.subjectId || "").toUpperCase()
    );
    const selectedTopic = (selectedSubject?.topics || []).find(
      (t) => String(t.topicName || "").toUpperCase() === String(baseForm.topicName || "").toUpperCase()
    );
    const selectedSub = (selectedTopic?.subTopics || []).find(
      (sub) => {
        const subName = typeof sub === "string" ? sub : (sub?.subTopicName || sub?.name || "");
        return String(subName).toUpperCase() === String(baseForm.subTopicName || "").toUpperCase();
      }
    );
    const selectedNested = (selectedSub?.nestedTopics || selectedSub?.childTopics || []).find(
      (nt) => String(nt.nestedTopicName || nt.name || "").toUpperCase() === String(baseForm.nestedTopicName || "").toUpperCase()
    );
    return (selectedNested?.nestedSubTopics || selectedNested?.childTopics || []).map((sst) => String(sst || "").toUpperCase()).filter(Boolean);
  }, [subjects, baseForm.subjectId, baseForm.topicName, baseForm.subTopicName, baseForm.nestedTopicName]);

  const fetchMasters = async () => {
    const [examRes, catRes, subRes] = await Promise.allSettled([
      axios.get("/master/exam"),
      axios.get("/master/category/all"),
      axios.get("/master/syllabus/all"),
    ]);
    setExams(examRes.status === "fulfilled" ? (Array.isArray(examRes.value.data) ? examRes.value.data : examRes.value.data?.data || []) : []);
    setCategories(catRes.status === "fulfilled" ? (Array.isArray(catRes.value.data) ? catRes.value.data : catRes.value.data?.data || []) : []);
    setSubjects(subRes.status === "fulfilled" ? (Array.isArray(subRes.value.data) ? subRes.value.data : subRes.value.data?.data || []) : []);
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.examMasterId) params.set("examMasterId", filters.examMasterId);
      if (filters.examCategoryId) params.set("examCategoryId", filters.examCategoryId);
      if (filters.examStage) params.set("examStage", filters.examStage);
      if (filters.subjectId) params.set("subjectId", filters.subjectId);
      if (filters.topicName) params.set("topicName", filters.topicName);
      if (filters.subTopicName) params.set("subTopicName", filters.subTopicName);
      if (filters.nestedTopicName) params.set("nestedTopicName", filters.nestedTopicName);
      if (filters.nestedSubTopicName) params.set("nestedSubTopicName", filters.nestedSubTopicName);
      if (filters.status) params.set("status", filters.status);
      if (searchTerm.trim()) params.set("search", searchTerm.trim());
      params.set("page", String(currentPage));
      params.set("limit", rowsPerPage === "ALL" ? "10000" : String(rowsPerPage));
      const res = await axios.get(`/master/question-bank/all?${params.toString()}`);
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(list);
    } catch {
      toastErrorOnce("FAILED TO LOAD QUESTION SETS");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMasters(); }, []);
  
  useEffect(() => {
    fetchRows();
  }, [
    filters.examMasterId, 
    filters.examCategoryId, 
    filters.examStage, 
    filters.subjectId, 
    filters.topicName, 
    filters.subTopicName, 
    filters.nestedTopicName, 
    filters.nestedSubTopicName, 
    filters.status, 
    currentPage, 
    rowsPerPage
  ]);

  useEffect(() => {
    if (isNewRoute) {
      openAddModal();
      return;
    }
    if (isEditRoute && id) {
      handleEdit(id);
      return;
    }
  }, [isNewRoute, isEditRoute, id]);

  const openAddModal = () => {
    setEditId("");
    setDraftQuestions([]);
    setBaseForm({ 
      examMasterId: "", 
      examCategoryId: "", 
      examStage: "", 
      subjectId: "", 
      topicName: "", 
      subTopicName: "", 
      nestedTopicName: "", 
      nestedSubTopicName: "", 
      status: "ACTIVE" 
    });
    setQuestionSetId("");
    setQuestionForm({ ...emptyQuestion });
    setEntryMode("MANUAL");
    setViewMode("form");
  };

  const handleEdit = async (rowId) => {
    try {
      const res = await axios.get(`/master/question-bank/${rowId}`);
      const row = res.data?.data;
      if (!row) return;
      setShowViewModal(false);
      setEditId(row._id);
      setQuestionSetId(row.questionSetId || "");
      setDraftQuestions(Array.isArray(row.questions) ? row.questions : []);
      setBaseForm({
        examMasterId: row.examMasterId || "",
        examCategoryId: row.examCategoryId || "",
        examStage: row.examStage || "",
        subjectId: row.subjectId || "",
        topicName: row.topicName || "",
        subTopicName: row.subTopicName || "",
        nestedTopicName: row.nestedTopicName || "",
        nestedSubTopicName: row.nestedSubTopicName || "",
        status: row.status || "ACTIVE",
      });
      setQuestionForm({ ...emptyQuestion });
      setEntryMode("MANUAL");
      setViewMode("form");

      setTimeout(() => {
        typesetMath(formMathRef.current);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 300); 
    } catch {
      toastErrorOnce("FAILED TO LOAD QUESTION SET");
    }
  };

  const handleView = async (rowId) => {
    try {
      const res = await axios.get(`/master/question-bank/${rowId}`);
      const row = res.data?.data;
      if (!row) return;
      setViewSet(row);
      setShowViewModal(true);
    } catch {
      toastErrorOnce("FAILED TO LOAD QUESTION SET");
    }
  };

  const validateQuestion = () => "";

  const readFilesAsDataUrls = (fileList) => {
    const files = Array.from(fileList || []);
    return Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ""));
            reader.onerror = () => reject(new Error("FILE_READ_FAILED"));
            reader.readAsDataURL(file);
          })
      )
    );
  };

  const appendImages = async (field, fileList, optionKey) => {
    if (!fileList || fileList.length === 0) return;
    try {
      const images = await readFilesAsDataUrls(fileList);
      setQuestionForm((prev) => {
        if (field === "optionImages") {
          const nextOption = { ...(prev.optionImages || {}) };
          nextOption[optionKey] = [ ...(nextOption[optionKey] || []), ...images ];
          return { ...prev, optionImages: nextOption };
        }
        return { ...prev, [field]: [ ...(prev[field] || []), ...images ] };
      });
    } catch {
      toastErrorOnce("IMAGE LOAD FAILED");
    }
  };

  const removeImage = (field, index, optionKey) => {
    setQuestionForm((prev) => {
      if (field === "optionImages") {
        const nextOption = { ...(prev.optionImages || {}) };
        nextOption[optionKey] = (nextOption[optionKey] || []).filter((_, i) => i !== index);
        return { ...prev, optionImages: nextOption };
      }
      return { ...prev, [field]: (prev[field] || []).filter((_, i) => i !== index) };
    });
  };

  const insertTextAtCursor = (fieldKey, text) => {
    const input = fieldRefs.current[fieldKey];
    if (!input) {
      setQuestionForm((prev) => ({ ...prev, [fieldKey]: `${prev[fieldKey] || ""}${text}` }));
      return;
    }
    const start = input.selectionStart ?? String(questionForm[fieldKey] || "").length;
    const end = input.selectionEnd ?? start;
    setQuestionForm((prev) => {
      const value = String(prev[fieldKey] || "");
      const nextValue = `${value.slice(0, start)}${text}${value.slice(end)}`;
      return { ...prev, [fieldKey]: nextValue };
    });
    setTimeout(() => {
      input.focus();
      const pos = start + text.length;
      if (input.setSelectionRange) input.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertSymbol = (symbol, fieldKey = activeField || "questionText") => {
    insertTextAtCursor(fieldKey, symbol);
  };

  const typesetMath = (root) => {
    if (!root) return;
    if (!window.MathJax || !window.MathJax.typesetPromise) {
      setTimeout(() => typesetMath(root), 200);
      return;
    }
    if (window.MathJax.typesetClear) window.MathJax.typesetClear([root]);
    window.MathJax.typesetPromise([root]);
  };

  useEffect(() => {
    clearTimeout(typesetTimerRef.current);
    typesetTimerRef.current = setTimeout(() => {
      typesetMath(formMathRef.current);
      if (document.activeElement === document.body && activeField) {
        const el = fieldRefs.current[activeField];
        if (el) el.focus();
      }
    }, 180);
  }, [questionForm.questionText, questionForm.explanationText, questionForm.optionA, questionForm.optionB, questionForm.optionC, questionForm.optionD, activeField]);

  useEffect(() => {
    if (showViewModal) typesetMath(viewMathRef.current);
  }, [showViewModal, viewSet]);

  const renderSymbolPicker = (fieldKey) => (
    <div className="space-y-1 p-1">
      <button
        type="button"
        onClick={() => setSymbolField((prev) => (prev === fieldKey ? "" : fieldKey))}
        className="flex items-center gap-1 px-2 py-1 bg-sky-100 hover:bg-sky-200 text-sky-800 rounded-lg transition-all border border-sky-200 text-xs font-bold"
      >
        <span>{symbolField === fieldKey ? "Hide Symbols" : "Symbols"}</span>
      </button>

      {symbolField === fieldKey && (
        <div className="flex flex-wrap items-center gap-1 rounded-xl border border-sky-200 bg-white p-1 shadow-sm">
          <div className="w-full mb-0.5">
            <span className="text-[10px] font-bold uppercase text-sky-600">
              Inserting to: <span className="text-slate-900">{fieldLabels[fieldKey] || "Question"}</span>
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {symbolPalette.map((sym, idx) => (
              <button
                key={`${fieldKey}-${idx}`}
                type="button"
                onClick={() => insertSymbol(sym, fieldKey)}
                className="px-2 py-0.5 text-xs font-bold bg-slate-50 border border-slate-200 text-slate-800 rounded hover:bg-sky-600 hover:text-white transition-all"
              >
                {sym}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const addDraftQuestion = () => {
    if (!questionForm.questionText) return toastErrorOnce("Question text is required");
    const finalQuestion = { 
      ...questionForm, 
      marks: questionForm.marks || "0", 
      negativeMarks: questionForm.negativeMarks || "0" 
    };

    setDraftQuestions((prev) => {
      if (editingDraftIndex === null || editingDraftIndex === undefined) {
        return [...prev, finalQuestion];
      }
      const next = [...prev];
      if (editingDraftIndex >= 0 && editingDraftIndex < next.length) {
        next[editingDraftIndex] = finalQuestion;
        return next;
      }
      return [...prev, finalQuestion];
    });
    
    setQuestionForm((prev) => ({
      ...emptyQuestion,
      marks: prev.marks,
      negativeMarks: prev.negativeMarks,
    }));
    setEditingDraftIndex(null);
    toastOkOnce("Added new question successfully!");
  };

  const editDraftQuestion = (question, index) => {
    setEntryMode("MANUAL");
    setQuestionForm((prev) => ({ ...prev, ...question }));
    setEditingDraftIndex(index);
    setTimeout(() => {
      questionEntryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      typesetMath(formMathRef.current);
    }, 0);
  };

  const handleExcelImport = async (file) => {
    if (!file) return;
    try {
      const defaultMarks = String(questionForm.marks || "").trim() || "0";
      const defaultNegativeMarks = String(questionForm.negativeMarks || "").trim() || "0";
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) return toastErrorOnce("NO SHEET FOUND");
      const sheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!rawRows.length) return toastErrorOnce("NO DATA FOUND");

      const imported = [];
      let skipped = 0;

      rawRows.forEach((row) => {
        const mappedRow = {};
        Object.keys(row || {}).forEach((header) => {
          const key = headerToKey(header);
          if (!key) return;
          mappedRow[key] = row[header];
        });

        const questionText = cleanMathText(mappedRow.questionText);
        const optionA = cleanMathText(mappedRow.optionA);
        const optionB = cleanMathText(mappedRow.optionB);
        const optionC = cleanMathText(mappedRow.optionC);
        const optionD = cleanMathText(mappedRow.optionD);
        const explanationText = cleanMathText(mappedRow.explanationText);

        const hasContent = Boolean(questionText || optionA || optionB || optionC || optionD || explanationText);
        if (!hasContent) {
          skipped += 1;
          return;
        }

        const rowMarks = String(mappedRow.marks ?? "").trim();
        const rowNegativeMarks = String(mappedRow.negativeMarks ?? "").trim();

        imported.push({
          marks: rowMarks || defaultMarks,
          negativeMarks: rowNegativeMarks || defaultNegativeMarks,
          questionText,
          optionA,
          optionB,
          optionC,
          optionD,
          correctOption: normalizeCorrectOption(mappedRow.correctOption),
          explanationText,
          questionImages: [],
          optionImages: { A: [], B: [], C: [], D: [] },
          explanationImages: [],
        });
      });

      if (!imported.length) {
        return toastErrorOnce("NO VALID QUESTIONS FOUND");
      }

      setDraftQuestions((prev) => [...prev, ...imported]);
      toastOkOnce(`IMPORTED ${imported.length} QUESTION${imported.length > 1 ? "S" : ""}${skipped ? `, SKIPPED ${skipped}` : ""}`);
    } catch {
      toastErrorOnce("EXCEL IMPORT FAILED");
    }
  };

  const saveBatch = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await axios.post("/master/question-bank/upsert", {
        questionSetId,
        ...baseForm,
        questions: draftQuestions,
      });
      toastOkOnce("QUESTION SET SAVED");
      await fetchRows();
      await openAddModal();
    } catch (err) {
      toastErrorOnce(err?.response?.data?.message || "SAVE FAILED");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    if (editId) {
      const err = validateQuestion(questionForm);
      if (err) return toastErrorOnce(err);
      setIsSaving(true);
      try {
        await axios.post("/master/question-bank/upsert", {
          id: editId,
          questionSetId,
          ...baseForm,
          questions: draftQuestions,
        });
        toastOkOnce("UPDATED");
        fetchRows();
        openAddModal();
      } catch (error) {
        toastErrorOnce(error?.response?.data?.message || "UPDATE FAILED");
      } finally {
        setIsSaving(false);
      }
      return;
    }
    await saveBatch();
  };

  const handleDelete = async (rowId) => {
    if (!window.confirm("ARE YOU SURE?")) return;
    try {
      await axios.delete(`/master/question-bank/${rowId}`);
      toastOkOnce("DELETED");
      fetchRows();
    } catch {
      toastErrorOnce("DELETE FAILED");
    }
  };

  const filteredData = rows.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const startsWithFirstLetter = (value) => {
      if (!value) return false;
      return value.trim().toLowerCase().startsWith(term);
    };
    return (
      startsWithFirstLetter(item.questionSetId) ||
      startsWithFirstLetter(item.examName) ||
      startsWithFirstLetter(item.subjectName) ||
      startsWithFirstLetter(item.status || "ACTIVE")
    );
  });

  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar p-1`}>
      {viewMode === "list" && (
        <>
          {/* HEADER CARD */}
          <div className={`${masterPanelCard} p-1 mb-1`}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <Search size={13} />
                </div>
                <h1 className={masterHeadingMain}>Question Sets</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={fetchRows}
                  className={masterBtnBack}
                  title="Refresh Records"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  <span>SYNC</span>
                </button>

                <button
                  type="button"
                  onClick={openAddModal}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>ADD QUESTION SET</span>
                </button>
              </div>
            </div>
          </div>

          {/* FILTERS CARD */}
          <div className={`${masterPanelCard} p-1 mb-1`}>
            <div className="space-y-1">
              <div className="grid grid-cols-1 md:grid-cols-9 gap-1">
                <select value={filters.examMasterId} onChange={(e) => { setFilters((prev) => ({ ...prev, examMasterId: e.target.value, examCategoryId: "", examStage: "", subjectId: "", topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })); setCurrentPage(1); }} className={`${masterSelect} p-1 text-xs`}>
                  <option value="">All Exams</option>
                  {exams.map((ex) => <option key={ex.examCode} value={ex.examCode}>{ex.examName}</option>)}
                </select>
                {filterNeedsCategory && <select value={filters.examCategoryId} onChange={(e) => { const categoryId = e.target.value; setFilters((prev) => ({ ...prev, examCategoryId: categoryId, examStage: "", subjectId: "", topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })); setCurrentPage(1); }} className={`${masterSelect} p-1 text-xs`}>
                  <option value="">All Categories</option>
                  {filteredCategories.map((cat) => <option key={cat.catId} value={cat.catId}>{cat.catName}</option>)}
                </select>}
                {filterNeedsStage && (
                  <select value={filters.examStage} onChange={(e) => { setFilters((prev) => ({ ...prev, examStage: e.target.value, subjectId: "", topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })); setCurrentPage(1); }} className={`${masterSelect} p-1 text-xs`}>
                    <option value="">All Stages</option>
                    {filterStageOptions.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                  </select>
                )}
                <select value={filters.subjectId} onChange={(e) => { setFilters((prev) => ({ ...prev, subjectId: e.target.value, topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })); setCurrentPage(1); }} className={`${masterSelect} p-1 text-xs`}>
                  <option value="">All Subjects</option>
                  {filteredSubjects.map((sub) => <option key={sub.syllabusId} value={sub.syllabusId}>{sub.subjectName}</option>)}
                </select>
                <select value={filters.topicName} onChange={(e) => { setFilters((prev) => ({ ...prev, topicName: e.target.value, subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })); setCurrentPage(1); }} className={`${masterSelect} p-1 text-xs`}>
                  <option value="">All Topics</option>
                  {filterTopicOptions.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
                </select>
                <select value={filters.subTopicName} onChange={(e) => { setFilters((prev) => ({ ...prev, subTopicName: e.target.value, nestedTopicName: "", nestedSubTopicName: "" })); setCurrentPage(1); }} className={`${masterSelect} p-1 text-xs`}>
                  <option value="">All SubTopics</option>
                  {filterSubTopicOptions.map((sub, idx) => {
                    const label = typeof sub === "string" ? sub : (sub.subTopicName || sub.name || "");
                    return <option key={idx} value={label}>{label}</option>;
                  })}
                </select>
                <select value={filters.nestedTopicName} onChange={(e) => { setFilters((prev) => ({ ...prev, nestedTopicName: e.target.value, nestedSubTopicName: "" })); setCurrentPage(1); }} className={`${masterSelect} p-1 text-xs`}>
                  <option value="">All Nested Topics</option>
                  {filterNestedTopicOptions.map((nt) => <option key={nt} value={nt}>{nt}</option>)}
                </select>
                <select value={filters.nestedSubTopicName} onChange={(e) => { setFilters((prev) => ({ ...prev, nestedSubTopicName: e.target.value })); setCurrentPage(1); }} className={`${masterSelect} p-1 text-xs`}>
                  <option value="">All Nested SubTopics</option>
                  {filterNestedSubTopicOptions.map((sst) => <option key={sst} value={sst}>{sst}</option>)}
                </select>
                <select value={filters.status} onChange={(e) => { setFilters((prev) => ({ ...prev, status: e.target.value })); setCurrentPage(1); }} className={`${masterSelect} p-1 text-xs`}>
                  <option value="">All Status</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
            </div>
          </div>

          {/* COMBINED SEARCH & PAGINATION BAR */}
          <PaginationSearchbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="SEARCH SET ID, EXAM, OR SUBJECT BY FIRST LETTER..."
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalItems={filteredData.length}
          />

          {/* TABLE CONTAINER */}
          <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col p-1`}>
            <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
              <table className={masterTable}>
                <thead>
                  <tr className={masterTableHead}>
                    <th className={masterThCode}>SL NO</th>
                    <th className={masterThName}>EXAM</th>
                    {hasStageInRows && <th className={masterThName}>STAGE</th>}
                    <th className={masterThName}>CATEGORY</th>
                    <th className={masterThName}>SUBJECT</th>
                    {hasTopicInRows && <th className={masterThName}>TOPIC</th>}
                    {hasTopicInRows && <th className={masterThName}>SUBTOPIC</th>}
                    {hasTopicInRows && <th className={masterThName}>NESTED TOPIC</th>}
                    {hasTopicInRows && <th className={masterThName}>NESTED SUBTOPIC</th>}
                    <th className={masterThName}>QUESTIONS</th>
                    <th className={masterThStatus}>STATUS</th>
                    <th className={masterThManage}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan={11} className={masterTdEmpty}>
                        Loading...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((row,index) => (
                      <tr key={row._id} className={masterTableRow}>
                        <td className={masterTdCode}>{startIndex + index + 1}</td>
                        <td className={masterTdName}>{row.examName}</td>
                        {hasStageInRows && <td className={masterTdName}>{row.examStage || "---"}</td>}
                        <td className={masterTdName}>{row.categoryName}</td>
                        <td className={masterTdName}>{row.subjectName}</td>
                        {hasTopicInRows && <td className={masterTdName}>{row.topicName || "---"}</td>}
                        {hasTopicInRows && <td className={masterTdName}>{row.subTopicName || "---"}</td>}
                        {hasTopicInRows && <td className={masterTdName}>{row.nestedTopicName || "---"}</td>}
                        {hasTopicInRows && <td className={masterTdName}>{row.nestedSubTopicName || "---"}</td>}
                        <td className={masterTdName}>{Array.isArray(row.questions) ? row.questions.length : 0}</td>
                        <td className={masterTdStatus}>
                          <span className={row.status === "INACTIVE" ? masterBadgeInactive : masterBadgeLive}>
                            {row.status}
                          </span>
                        </td>
                        <td className={masterTdManage}>
                          <div className={masterTdManageFlex}>
                            <button type="button" onClick={() => handleView(row._id)} className={masterActionBtnEdit} title="View">
                              <Eye size={13} />
                            </button>
                            <button type="button" onClick={() => handleEdit(row._id)} className={masterActionBtnEdit} title="Edit">
                              <Edit size={13} />
                            </button>
                            <button type="button" onClick={() => handleDelete(row._id)} className={masterActionBtnDelete} title="Delete">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={11} className={masterTdEmpty}>
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showViewModal && viewSet && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-1">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => { setShowViewModal(false); setViewSet(null); }}></div>
          <div ref={viewMathRef} className="relative bg-white w-full max-w-5xl p-3 rounded-[16px] shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-sm font-bold uppercase tracking-tight text-slate-900">Question Set View</h2>
                <p className="text-[10px] text-slate-500 font-bold">Set ID: <span className="font-bold text-blue-600">{viewSet.questionSetId}</span></p>
              </div>
              <button type="button" onClick={() => { setShowViewModal(false); setViewSet(null); }} className="p-1 hover:bg-slate-100 rounded-full"><X size={16} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-slate-700 mb-2 bg-slate-50 p-2 rounded-xl border border-slate-200 font-bold">
              <div><span className="text-slate-500 uppercase">Exam:</span> {viewSet.examName || "---"}</div>
              <div><span className="text-slate-500 uppercase">Category:</span> {viewSet.categoryName || "---"}</div>
              <div><span className="text-slate-500 uppercase">Subject:</span> {viewSet.subjectName || "---"}</div>
            </div>

            <div className="space-y-2">
              {(Array.isArray(viewSet.questions) ? viewSet.questions : []).map((q, idx) => (
                <div key={`view-q-${idx}`} className={`${masterSubCard} p-2`}>
                  <div className="flex items-center justify-between text-xs text-slate-500 font-bold">
                    <div className="text-slate-900">{q.qNo || `Q${idx + 1}`}</div>
                    <div>Marks: <span className="text-slate-900">{q.marks ?? "--"}</span> | Neg: <span className="text-slate-900">{q.negativeMarks ?? "--"}</span></div>
                  </div>

                  <div className="text-xs text-slate-900 font-semibold whitespace-pre-wrap">{q.questionText || "---"}</div>

                  {(Array.isArray(q.questionImages) ? q.questionImages : []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {q.questionImages.map((img, i) => (
                        <img key={`vqimg-${idx}-${i}`} src={img} alt="question" className="h-16 w-16 object-cover rounded border border-slate-200" />
                      ))}
                    </div>
                  )}

                  <div className="space-y-1">
                    {["A", "B", "C", "D"].map((opt) => (
                      <div key={`view-${idx}-${opt}`} className={`${masterInnerCard} p-1.5`}>
                        <div className="text-[10px] font-bold text-slate-600 mb-0.5">Option {opt}</div>
                        <div className="text-xs text-slate-900 font-semibold whitespace-pre-wrap">{q[`option${opt}`] || "---"}</div>
                        {(Array.isArray(q.optionImages?.[opt]) ? q.optionImages?.[opt] : []).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {q.optionImages[opt].map((img, i) => (
                              <img key={`vopt-${idx}-${opt}-${i}`} src={img} alt={`option-${opt}`} className="h-14 w-14 object-cover rounded border border-slate-200" />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="text-xs font-bold text-emerald-700 bg-emerald-50 p-1.5 rounded-xl border border-emerald-100">
                    Correct Answer: <span className="text-slate-900">{q.correctOption || "--"}</span>
                  </div>

                  <div className="text-xs text-slate-800 bg-slate-50 p-2 rounded-xl border border-slate-200">
                    <div className="text-[10px] font-bold text-slate-500 uppercase mb-0.5">Explanation</div>
                    <div className="font-semibold whitespace-pre-wrap">{q.explanationText || "---"}</div>
                  </div>

                  {(Array.isArray(q.explanationImages) ? q.explanationImages : []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {q.explanationImages.map((img, i) => (
                        <img key={`vexp-${idx}-${i}`} src={img} alt="explanation" className="h-16 w-16 object-cover rounded border border-slate-200" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {(Array.isArray(viewSet.questions) ? viewSet.questions : []).length === 0 && (
                <div className="p-6 text-center text-slate-300 font-bold uppercase text-xs">No questions in this set.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {viewMode === "form" && (
        <>
          <div className={`${masterPanelCard} p-1 mb-1 sticky top-0 z-20 shadow-md`}>
            <div className="flex items-center justify-between gap-1 w-full">
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <Edit size={13} />
                </div>
                <h1 className={`${masterHeadingMain} whitespace-nowrap`}>{editId ? "Update Question Set" : "Create Question Set"}</h1>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="submit" form="question-set-form" disabled={isSaving} className={`${masterFormBtnSubmit} whitespace-nowrap`}>
                  {isSaving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
                  <span>{editId ? "Final Update" : "Final Save"}</span>
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className={`${masterFormBtnSubmit} whitespace-nowrap`}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>VIEW LIST</span>
                </button>
              </div>
            </div>
          </div>

          <div className="p-1">
            <form id="question-set-form" ref={formMathRef} onSubmit={handleSubmit} dir="ltr" className={`${masterFormCard} p-2 space-y-2`}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-1">
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Exam</label>
                  <select value={baseForm.examMasterId} onChange={(e) => setBaseForm({ ...baseForm, examMasterId: e.target.value, examCategoryId: "", examStage: "", subjectId: "", topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })} className={`${masterSelect} p-1 text-xs`}>
                    <option value="">-- Select Exam --</option>
                    {exams.map((ex) => <option key={ex.examCode} value={ex.examCode}>{ex.examName}</option>)}
                  </select>
                </div>
                {modalNeedsCategory && (
                  <div className={masterFormFieldGroup}>
                    <label className={masterInputLabel}>Category</label>
                    <select value={baseForm.examCategoryId} onChange={(e) => { const categoryId = e.target.value; setBaseForm({ ...baseForm, examCategoryId: categoryId, examStage: "", subjectId: "", topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" }); }} className={`${masterSelect} p-1 text-xs`}>
                      <option value="">-- Select Category --</option>
                      {modalFilteredCategories.map((cat) => <option key={cat.catId} value={cat.catId}>{cat.catName}</option>)}
                    </select>
                  </div>
                )}
                {modalNeedsStage && (
                  <div className={masterFormFieldGroup}>
                    <label className={masterInputLabel}>Exam Stage</label>
                    <select value={baseForm.examStage} onChange={(e) => setBaseForm({ ...baseForm, examStage: e.target.value, subjectId: "", topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })} className={`${masterSelect} p-1 text-xs`}>
                      <option value="">-- Select Stage --</option>
                      {modalStageOptions.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                    </select>
                  </div>
                )}
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Subject</label>
                  <select value={baseForm.subjectId} onChange={(e) => setBaseForm({ ...baseForm, subjectId: e.target.value, topicName: "", subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })} className={`${masterSelect} p-1 text-xs`}>
                    <option value="">-- Select Subject --</option>
                    {modalFilteredSubjects.map((sub) => <option key={sub.syllabusId} value={sub.syllabusId}>{sub.subjectName}</option>)}
                  </select>
                </div>
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Topic</label>
                  <select value={baseForm.topicName} onChange={(e) => setBaseForm({ ...baseForm, topicName: e.target.value, subTopicName: "", nestedTopicName: "", nestedSubTopicName: "" })} className={`${masterSelect} p-1 text-xs`}>
                    <option value="">-- Select Topic --</option>
                    {modalTopicOptions.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
                  </select>
                </div>
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>SubTopic</label>
                  <select value={baseForm.subTopicName} onChange={(e) => setBaseForm({ ...baseForm, subTopicName: e.target.value, nestedTopicName: "", nestedSubTopicName: "" })} className={`${masterSelect} p-1 text-xs`}>
                    <option value="">-- Select SubTopic --</option>
                    {modalSubTopicOptions.map((sub, idx) => {
                      const label = typeof sub === "string" ? sub : (sub.subTopicName || sub.name || "");
                      return <option key={idx} value={label}>{label}</option>;
                    })}
                  </select>
                </div>
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Nested Topic</label>
                  <select value={baseForm.nestedTopicName} onChange={(e) => setBaseForm({ ...baseForm, nestedTopicName: e.target.value, nestedSubTopicName: "" })} className={`${masterSelect} p-1 text-xs`}>
                    <option value="">-- Select Nested Topic --</option>
                    {modalNestedTopicOptions.map((nt) => <option key={nt} value={nt}>{nt}</option>)}
                  </select>
                </div>
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Nested SubTopic</label>
                  <select value={baseForm.nestedSubTopicName} onChange={(e) => setBaseForm({ ...baseForm, nestedSubTopicName: e.target.value })} className={`${masterSelect} p-1 text-xs`}>
                    <option value="">-- Select Nested SubTopic --</option>
                    {modalNestedSubTopicOptions.map((sst) => <option key={sst} value={sst}>{sst}</option>)}
                  </select>
                </div>
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Status</label>
                  <select value={baseForm.status} onChange={(e) => setBaseForm({ ...baseForm, status: e.target.value })} className={`${masterSelect} p-1 text-xs`}>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2">
                <label className={`flex items-center gap-2 h-8 px-3 rounded-lg border text-[10px] font-bold uppercase cursor-pointer transition ${entryMode === "MANUAL" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-700"}`}>
                  <input
                    type="checkbox"
                    checked={entryMode === "MANUAL"}
                    onChange={() => setEntryMode("MANUAL")}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Manual Entry</span>
                </label>
                <label className={`flex items-center gap-2 h-8 px-3 rounded-lg border text-[10px] font-bold uppercase cursor-pointer transition ${entryMode === "EXCEL" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-700"}`}>
                  <input
                    type="checkbox"
                    checked={entryMode === "EXCEL"}
                    onChange={() => setEntryMode("EXCEL")}
                    className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>Import from Excel</span>
                </label>
              </div>

              {entryMode === "EXCEL" && (
                <div className={`${masterSubCard} p-2`}>
                  <div className="border border-dashed border-slate-300 rounded-xl p-3 bg-white">
                    <div className="text-[10px] font-bold text-slate-900 uppercase">Import from Excel</div>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        handleExcelImport(file);
                        e.target.value = "";
                      }}
                      className="mt-2 w-full p-1 border border-slate-200 rounded-lg text-xs"
                    />
                  </div>
                </div>
              )}

              {entryMode === "MANUAL" && (
              <div ref={questionEntryRef} className={`${masterSubCard} p-2 scroll-mt-20`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
                  <div className={masterFormFieldGroup}><label className={masterInputLabel}>Marks / Question</label><input type="number" step="0.5" value={questionForm.marks} onChange={(e) => setQuestionForm((prev) => ({ ...prev, marks: e.target.value }))} className={`${masterInput} p-1`} /></div>
                  <div className={masterFormFieldGroup}><label className={masterInputLabel}>Negative Marks</label><input type="number" step="0.25" value={questionForm.negativeMarks} onChange={(e) => setQuestionForm((prev) => ({ ...prev, negativeMarks: e.target.value }))} className={`${masterInput} p-1`} /></div>
                </div>

                {renderSymbolPicker("questionText")}
                <MathField
                  fieldKey="questionText"
                  label="Question"
                  value={questionForm.questionText}
                  onChange={(v) => setQuestionForm((prev) => ({ ...prev, questionText: v }))}
                  onPaste={(e) => handlePasteCleaning(e, questionForm.questionText, (nextValue) => setQuestionForm((prev) => ({ ...prev, questionText: nextValue })))}
                  placeholder="Question"
                  rows={4}
                />

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Question Images</label>
                  <input type="file" accept="image/*" multiple onChange={(e) => appendImages("questionImages", e.target.files)} className="w-full p-1 border border-slate-200 rounded-lg text-xs" />
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(questionForm.questionImages || []).map((img, idx) => (
                      <div key={`qimg-${idx}`} className="relative w-12 h-12 border border-slate-200 rounded overflow-hidden">
                        <img src={img} alt="question" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeImage("questionImages", idx)} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5"><X size={10} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  <div className={masterFormFieldGroup}>
                    <div className="flex items-center justify-between mb-0.5">
                      <label className={masterInputLabel}>Option A</label>
                      {renderSymbolPicker("optionA")}
                    </div>
                    <MathField fieldKey="optionA" value={questionForm.optionA} onChange={(v) => setQuestionForm((prev) => ({ ...prev, optionA: v }))} placeholder="Enter Option A" rows={2} />
                  </div>
                  <div className={masterFormFieldGroup}>
                    <div className="flex items-center justify-between mb-0.5">
                      <label className={masterInputLabel}>Option B</label>
                      {renderSymbolPicker("optionB")}
                    </div>
                    <MathField fieldKey="optionB" value={questionForm.optionB} onChange={(v) => setQuestionForm((prev) => ({ ...prev, optionB: v }))} placeholder="Enter Option B" rows={2} />
                  </div>
                  <div className={masterFormFieldGroup}>
                    <div className="flex items-center justify-between mb-0.5">
                      <label className={masterInputLabel}>Option C</label>
                      {renderSymbolPicker("optionC")}
                    </div>
                    <MathField fieldKey="optionC" value={questionForm.optionC} onChange={(v) => setQuestionForm((prev) => ({ ...prev, optionC: v }))} placeholder="Enter Option C" rows={2} />
                  </div>
                  <div className={masterFormFieldGroup}>
                    <div className="flex items-center justify-between mb-0.5">
                      <label className={masterInputLabel}>Option D</label>
                      {renderSymbolPicker("optionD")}
                    </div>
                    <MathField fieldKey="optionD" value={questionForm.optionD} onChange={(v) => setQuestionForm((prev) => ({ ...prev, optionD: v }))} placeholder="Enter Option D" rows={2} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {["A", "B", "C", "D"].map((opt) => (
                    <div key={`optimg-${opt}`} className={masterFormFieldGroup}>
                      <label className={masterInputLabel}>Option {opt} Images</label>
                      <input type="file" accept="image/*" multiple onChange={(e) => appendImages("optionImages", e.target.files, opt)} className="w-full p-1 border border-slate-200 rounded-lg text-xs" />
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(questionForm.optionImages?.[opt] || []).map((img, idx) => (
                          <div key={`opt-${opt}-${idx}`} className="relative w-12 h-12 border border-slate-200 rounded overflow-hidden">
                            <img src={img} alt={`option-${opt}`} className="w-full h-full object-cover" />
                            <button type="button" onClick={() => removeImage("optionImages", idx, opt)} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5"><X size={10} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Correct Option</label>
                  <select value={questionForm.correctOption} onChange={(e) => setQuestionForm((prev) => ({ ...prev, correctOption: e.target.value }))} className={`${masterSelect} p-1 text-xs`}>
                    <option value="">-- Select --</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                  </select>
                </div>

                {renderSymbolPicker("explanationText")}
                <MathField
                  fieldKey="explanationText"
                  label="Explanation"
                  value={questionForm.explanationText}
                  onChange={(v) => setQuestionForm((prev) => ({ ...prev, explanationText: v }))}
                  onPaste={(e) => handlePasteCleaning(e, questionForm.explanationText, (nextValue) => setQuestionForm((prev) => ({ ...prev, explanationText: nextValue })))}
                  placeholder="Explanation"
                  rows={3}
                />

                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Explanation Images</label>
                  <input type="file" accept="image/*" multiple onChange={(e) => appendImages("explanationImages", e.target.files)} className="w-full p-1 border border-slate-200 rounded-lg text-xs" />
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(questionForm.explanationImages || []).map((img, idx) => (
                      <div key={`eimg-${idx}`} className="relative w-12 h-12 border border-slate-200 rounded overflow-hidden">
                        <img src={img} alt="explanation" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeImage("explanationImages", idx)} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5"><X size={10} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                <button type="button" onClick={addDraftQuestion} className={`${masterFormBtnSubmit} p-1 text-xs`}>Add</button>
              </div>
              )}

              {/* LIVE PREVIEW */}
              {entryMode === "MANUAL" && (
              <div className="border border-slate-200 rounded-xl p-2 bg-white space-y-2 shadow-inner">
                <div className="text-[10px] font-bold text-blue-600 uppercase tracking-widest border-b pb-1">Live Preview</div>
                <div className="space-y-2">
                  <div className="text-xs text-slate-900 font-semibold bg-slate-50 p-2 rounded-lg border border-slate-100">
                    <span className="font-bold text-slate-500 block text-[9px] uppercase mb-0.5">Question Text</span>
                    <div className="whitespace-pre-wrap">{questionForm.questionText || "---"}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {["A", "B", "C", "D"].map((letter) => (
                      <div key={letter} className="text-xs p-2 bg-white rounded-lg border border-slate-200 font-semibold">
                        <span className="font-bold text-blue-600 mr-1.5">{letter}.</span>
                        <span className="whitespace-pre-wrap">{questionForm[`option${letter}`] || "---"}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <div className="text-[11px] text-emerald-700 font-bold bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100">CORRECT: {questionForm.correctOption || "NONE"}</div>
                    <div className="text-[11px] text-blue-700 font-bold bg-blue-50 px-2 py-1 rounded-lg border border-blue-100">MARKS: {questionForm.marks || "0"}</div>
                  </div>
                  {questionForm.explanationText && (
                    <div className="text-xs text-slate-700 bg-slate-50 p-2 rounded-lg border-l-4 border-blue-400 font-semibold">
                      <span className="font-bold text-slate-500 block text-[9px] uppercase mb-0.5">Explanation</span>
                      <div className="whitespace-pre-wrap italic">{questionForm.explanationText}</div>
                    </div>
                  )}
                </div>
              </div>
              )}

              <div className="space-y-1">
                {draftQuestions.map((q, idx) => (
                  <div key={`draft-${idx}`} className={`${masterInnerCard} p-1`}>
                    <div>
                      <p className="text-xs font-bold text-blue-600">{`Q${idx + 1}`}</p>
                      <p className="text-xs font-bold text-slate-900 uppercase">{q.questionText || "---"}</p>
                    </div>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => editDraftQuestion(q, idx)} className={masterActionButton}><Edit size={13} /></button>
                      <button type="button" onClick={() => setDraftQuestions((prev) => prev.filter((_, i) => i !== idx))} className={masterDeleteButton}><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default QuestionBankMaster;